import * as React from "react";
import { alpha } from "@mui/material/styles";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { IUserMessage } from "../../../types/user-info/user-message";
import { useAppDispatch } from "../../../Hooks";
import { GetFileRequestMessage } from "../../../services/api-actions/personal-user-request-action";
import { brand } from "../../../theme";

type ChatBubbleProps = IUserMessage & {
  variant: "sent" | "received";
  sender: string;
};

// «ИП» из «Иванов Пётр», либо первая буква почты
function initials(sender: string) {
  const parts = sender.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function ChatBubble(props: ChatBubbleProps) {
  const dispatch = useAppDispatch();
  const { message, variant, createdDate, fileName, sender, id } = props;
  const isSent = variant === "sent";

  return (
    <Stack direction={isSent ? "row-reverse" : "row"} gap={1.5} alignItems="flex-end" sx={{ maxWidth: "100%" }}>
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: 14,
          fontWeight: 700,
          flexShrink: 0,
          bgcolor: isSent ? brand.main : alpha(brand.slate, 0.12),
          color: isSent ? "#fff" : brand.slate,
        }}
      >
        {isSent ? "Я" : initials(sender)}
      </Avatar>
      <Box sx={{ maxWidth: { xs: "82%", md: "70%" }, minWidth: 0 }}>
        <Stack direction="row" gap={1} alignItems="baseline" justifyContent={isSent ? "flex-end" : "flex-start"} sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: brand.ink }}>{sender}</Typography>
          <Typography sx={{ fontSize: 12, color: brand.muted }}>{dayjs(createdDate)?.format("HH:mm")}</Typography>
        </Stack>
        <Box
          sx={{
            p: 1.5,
            px: 2,
            borderRadius: 3,
            borderTopRightRadius: isSent ? "4px" : undefined,
            borderTopLeftRadius: isSent ? undefined : "4px",
            bgcolor: isSent ? brand.main : "#fff",
            color: isSent ? "#fff" : brand.ink,
            border: isSent ? "none" : `1px solid ${brand.line}`,
            boxShadow: isSent ? "none" : "0 1px 2px rgba(16,24,40,.05)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          <Typography sx={{ fontSize: 14.5, lineHeight: 1.55, color: "inherit" }}>{message}</Typography>
        </Box>
        {fileName != null && (
          <Button
            onClick={() => dispatch(GetFileRequestMessage({ messageId: id, fileName: fileName }))}
            startIcon={<InsertDriveFileOutlinedIcon />}
            endIcon={<DownloadIcon fontSize="small" />}
            sx={{
              mt: 0.75,
              maxWidth: "100%",
              justifyContent: "flex-start",
              bgcolor: "#fff",
              border: `1px solid ${brand.line}`,
              color: brand.ink,
              "&:hover": { borderColor: brand.main, color: brand.main, bgcolor: "#fff" },
            }}
          >
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13.5 }}>
              {fileName}
            </Box>
          </Button>
        )}
      </Box>
    </Stack>
  );
}
